/* eslint-disable @typescript-eslint/no-explicit-any */
import { isStringArray } from '@precon-tools-suite-by-shared/js-utils';
import type {
    DefaultStyles,
    LogCSSStyle,
    LogFn,
    PrettyFn,
    PrettyLogger,
    PrettyLoggerFn,
    PrettyPart,
} from './types';
import _ from 'lodash';
import { isPretty, isPrettyPartIterable } from './typeguards';
import { PrettyBuilder } from './PrettyBuilder';

function styleToStr(style?: LogCSSStyle): string {
    if (!style) return '';
    if (typeof style === 'string') return style;
    return Object.entries(style)
        .map(([k, v]) => `${_.kebabCase(k)}:${v}`)
        .join(';');
}

function partsToArgs(
    parts: Iterable<PrettyPart>,
    defaultStyles?: DefaultStyles
) {
    const specifiers: (
        | 'c'
        | 's'
        | 'i'
        | 'd'
        | 'f'
        | `.${number}f`
        | 'o'
        | 'O'
    )[] = [];
    const args: any[] = [];
    const ds = defaultStyles ?? {};

    const pushStyle = (style?: LogCSSStyle, styles: LogCSSStyle[] = []) => {
        specifiers.push('c');
        const s = [
            styleToStr(style),
            ...styles.map(styleToStr).filter((s) => s.trim() !== ''),
        ].join(';');
        args.push(s);
    };

    parts = [...parts];

    for (const part of parts) {
        switch (part.specifier) {
            case 'c':
                pushStyle(part.style, part.styles);
                break;
            case 's':
                if (part.style !== true)
                    pushStyle(part.style ?? ds.s, part.styles);
                specifiers.push('s');
                args.push(part.value);
                break;
            case 'i':
            case 'd':
                if (part.style !== true)
                    pushStyle(
                        part.style ?? ds[part.specifier] ?? ds.i ?? ds.d,
                        part.styles
                    );
                specifiers.push(part.specifier);
                args.push(part.value);
                break;
            case 'f':
                if (part.style !== true)
                    pushStyle(part.style ?? ds.f ?? ds.i ?? ds.d, part.styles);
                specifiers.push(
                    part.precision !== undefined ? `.${part.precision}f` : 'f'
                );
                args.push(part.value);
                break;
            case 'o':
            case 'O':
            default:
                specifiers.push(part.specifier);
                args.push(part.value);
                break;
        }
    }

    return [specifiers.map((s) => '%' + s).join(''), ...args];
}

function resolvePrettyFn(fn: PrettyFn): Iterable<PrettyPart> {
    const $ = PrettyBuilder;
    const x = fn($);

    if (x?.__isPrettyBuilder) return x;

    if (isPretty(x)) return x.__pretty($);
    if (isPrettyPartIterable(x)) return x;

    if (x === null) return $('null', $.bold, $.mono, $.dim);

    if (x === undefined)
        return $('[', $.dim, $.xs)
            .s('undefined', $.italic, $.dim, $.xs)
            .s(']', $.dim, $.xs);

    if (typeof x === 'function')
        return x.name
            ? $(x.name, {
                  fontWeight: 'bold',
                  color: '#887700',
              })
            : $('[anonymous]', {
                  color: '#867d4e',
                  fontSize: 'small',
              });

    return [{ specifier: 'o', value: x }];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toPrettyParts(value: any): Iterable<PrettyPart> {
    if (typeof value === 'string')
        return [{ specifier: 's', value, style: true }];
    if (isPretty(value)) return value.__pretty(PrettyBuilder);
    if (typeof value === 'function') return resolvePrettyFn(value);
    return resolvePrettyFn(() => value);
}

export interface CreatePrettyLoggerOptions {
    textStyle?: LogCSSStyle;
    defaultStyles?: DefaultStyles;
}

export function createPrettyLogger(
    options: CreatePrettyLoggerOptions = {}
): PrettyLogger {
    function* textParts(value: string): Generator<PrettyPart> {
        if (value !== '')
            yield { specifier: 's', value, style: options.textStyle };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function* templateLiteralParts(
        strings: string[],
        ...expressions: any[]
    ): Generator<PrettyPart, void, undefined> {
        let i = 0;
        for (; i < expressions.length; i++) {
            yield* textParts(strings[i]);
            yield* toPrettyParts(expressions[i]);
        }
        yield* textParts(strings[i]);
    }

    const fn = (logger: LogFn) =>
        ((arg0: unknown, ...args: (string | any)[]) =>
            Array.isArray(arg0) &&
            arg0.length > 0 &&
            arg0.length === args.length + 1 &&
            isStringArray(arg0)
                ? (...extraArgs: any[]) =>
                      logger(
                          ...partsToArgs(templateLiteralParts(arg0, ...args)),
                          ...extraArgs
                      )
                : logger(arg0, ...args)) as PrettyLoggerFn;

    return Object.assign(fn(console.log), {
        debug: fn((...args) => console.debug(...args)),
        info: fn((...args) => console.info(...args)),
        log: fn((...args) => console.log(...args)),
        warn: fn((...args) => console.warn(...args)),
        error: fn((...args) => console.error(...args)),
        group: fn((...args) => console.group(...args)),
        groupCollapsed: fn((...args) => console.groupCollapsed(...args)),
    });
}
